// Build the tenant theme from one color. Keep text and controls readable for pale brands too.
import { palette } from '@primevue/themes';
import tinycolor from 'tinycolor2';
import ZIGGU_GREEN from '@/app/data/themes_constants.js';

// Tint Tailwind's neutral scale with the brand hue.
const NEUTRAL_GRAY = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0a0a0a',
};
const NEUTRAL_SAT = 0.1; // 0 is pure gray
const neutral = (step, hue) =>
  tinycolor({ h: hue, s: NEUTRAL_SAT, l: tinycolor(NEUTRAL_GRAY[step]).toHsl().l }).toHexString();

const AA_TEXT = 4.5;
const MIN_INDICATOR = 3; // WCAG minimum for controls
const MAX_NUDGE = 10; // % lightness we may take off a brand color before the change shows

// Buy the last bit of contrast from the fill instead of the text. A brand that lands just under AA
// (ION's red at 4.38) gives up a few points of lightness so white clears the bar. A pale brand would
// have to go nearly black to get there, so it keeps its color and takes dark ink instead.
function fillSeed(seed) {
  for (let i = 0; i <= MAX_NUDGE; i += 1) {
    // Darken the seed itself each time. Stepping one color down repeatedly round trips through RGB
    // and drifts, which overshoots the cap and nudges the hue the neutrals are built from. Writing
    // the result back as a hex can still land a fifth of a point past the cap; that is 8-bit
    // rounding, not the loop.
    const fill = tinycolor(seed).darken(i);

    if (tinycolor.readability('#ffffff', fill) >= AA_TEXT) return fill.toHexString();
  }

  return seed;
}

// A darkened version of the fill, taken far enough down to read on it. A fill can be too dark for
// any ink of its own hue to clear AA, and there white is the only readable answer left.
function readableInk(fill) {
  const ink = tinycolor(fill).darken(35);

  for (let i = 0; i < 20 && tinycolor.readability(ink, fill) < AA_TEXT; i += 1) {
    ink.darken(4);
  }

  return tinycolor.readability(ink, fill) >= AA_TEXT ? ink.toHexString() : '#ffffff';
}

// One decision for the whole button, taken on the base fill. Hover and active are darker versions of
// it, so white only reads better there. Deciding per fill made the label flip color under the cursor.
// A pale brand can still run out of dark ink on the darkest fill, and there a flip beats unreadable.
function inksFor(pal) {
  if (tinycolor.readability('#ffffff', pal[500]) >= AA_TEXT) {
    return { 500: '#ffffff', 600: '#ffffff', 700: '#ffffff' };
  }

  return { 500: readableInk(pal[500]), 600: readableInk(pal[600]), 700: readableInk(pal[700]) };
}

// Darken or lighten a color until it has enough contrast with the background.
function accessibleOn(seed, bg, target = AA_TEXT) {
  const darken = tinycolor(bg).getLuminance() > 0.5;
  const c = tinycolor(seed);

  for (let i = 0; i < 15 && tinycolor.readability(c, bg) < target; i += 1) {
    if (darken) c.darken(4);
    else c.lighten(4);
  }

  return c.toHexString();
}

// Make pale selected states visible. Keep gray seeds gray.
function brandTint(seed, l) {
  const { h, s } = tinycolor(seed).toHsl();
  return tinycolor({ h, s: s < 0.05 ? s : Math.min(Math.max(s, 0.3), 0.5), l }).toHexString();
}

// Bulma needs separate hue, saturation and lightness values.
function applyBulmaPrimary(primary, contrast) {
  const { h, s, l } = tinycolor(primary).toHsl();
  const root = document.documentElement.style;

  root.setProperty('--bulma-primary-h', h);
  root.setProperty('--bulma-primary-s', `${s * 100}%`);
  root.setProperty('--bulma-primary-l', `${l * 100}%`);

  // Bulma buttons and tags build their text from the primary hue and this lightness alone, so the
  // composed --bulma-primary-invert never reaches them. Our ink is white (100% reads white at any
  // hue) or a darkened primary whose hue and saturation already match, so only the lightness travels.
  root.setProperty('--bulma-primary-invert-l', `${tinycolor(contrast).toHsl().l * 100}%`);
}

// Set the design system tokens and return the colors Buefy needs.
export function applyPrimary(seed, dark = false) {
  if (!isValidHex(seed)) return;

  const root = document.documentElement.style;
  // One brand color for the whole theme, nudged if white button text needs it.
  const brand = fillSeed(seed);
  const pal = palette(brand);

  for (const [step, hex] of Object.entries(pal)) {
    root.setProperty(`--color-primary-${step}`, hex);
  }

  // Tint shadows with the darkest brand shade.
  const { r, g, b } = tinycolor(pal[950]).toRgb();
  root.setProperty('--shadow-color', `${r} ${g} ${b}`);

  const hue = tinycolor(brand).toHsl().h;

  for (const step of Object.keys(NEUTRAL_GRAY)) {
    root.setProperty(`--color-neutral-${step}`, neutral(step, hue));
  }

  // 500 for normal, 600 for hover, 700 for active.
  const ink = inksFor(pal);

  root.setProperty('--color-primary-contrast', ink[500]);
  root.setProperty('--color-primary-contrast-600', ink[600]);
  root.setProperty('--color-primary-contrast-700', ink[700]);

  // Make links readable on the card.
  const bg_card = dark ? neutral(900, hue) : '#ffffff';

  root.setProperty('--color-link', accessibleOn(brand, bg_card));
  root.setProperty('--color-text-selection', brandTint(brand, dark ? 0.3 : 0.8));

  // Controls need a lower contrast ratio than text.
  root.setProperty('--color-focus-ring', accessibleOn(pal[400], bg_card, MIN_INDICATOR));
  root.setProperty('--color-accent', accessibleOn(pal[500], bg_card, MIN_INDICATOR));

  // Pale brands need stronger selected colors. Dark mode uses the preset values.
  if (dark) {
    root.removeProperty('--p-app-selected');
    root.removeProperty('--p-app-selected-hover');
  } else {
    root.setProperty('--p-app-selected', brandTint(brand, 0.92));
    root.setProperty('--p-app-selected-hover', brandTint(brand, 0.88));
  }

  return { primary: pal[500], contrast: ink[500] };
}

// Apply the same primary color and text color to Buefy and PrimeVue.
export default function applyTheme(theme_primary_color) {
  // Fall back out loud. A tenant whose stored colour is junk gets Ziggu green because we say so,
  // not because doing nothing happens to leave the stylesheet default in place.
  const color = tinycolor(theme_primary_color);
  const seed = color.isValid() ? color.toHexString() : ZIGGU_GREEN.hex_500;

  const { primary, contrast } = applyPrimary(seed);
  applyBulmaPrimary(primary, contrast);
}

// The one rule for what counts as a theme colour: a full six digit hex code.
export function isValidHex(hex) {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}
